import { useState } from "react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SlotList } from "@/components/slot/SlotList";
import {
  EdgesUsageSection,
  KovaniaUsageSection,
} from "@/components/zakazka/UsageSummarySections";
import { PorezPanel } from "@/components/varianta/PorezPanel";
import { ZakazkaSummary } from "@/components/zakazka/ZakazkaSummary";
import type { MaterialSlot, Varianta, ZakazkaDetail } from "@/api/schemas";
import type { CielOznacenia } from "@/lib/oznacenieRiadkov";

const LS_TAB = "tandem_panel_tab";

const TABY = [
  { key: "sloty", label: "Materiály", title: "Materiály zákazky (M1, M2…)" },
  { key: "hrany", label: "Hrany", title: "ABS hrany použité v zákazke" },
  { key: "kovania", label: "Kovania", title: "Kovania použité v zákazke" },
  {
    key: "rezanie",
    label: "Rezanie",
    title: "Porez tabúľ + nárezový plán dĺžkových",
  },
] as const;

type PanelTab = (typeof TABY)[number]["key"];

function readTab(): PanelTab {
  try {
    const v = localStorage.getItem(LS_TAB);
    if (TABY.some((t) => t.key === v)) return v as PanelTab;
  } catch {
    /* localStorage nemusí byť dostupný (private mode) */
  }
  return "sloty";
}

export interface BocnyPanelProps {
  zakazka: ZakazkaDetail;
  varianta: Varianta;
  sloty: MaterialSlot[];
  /** Slot IDs, ktoré používa aspoň jeden dielec — zvyšné sú „nepriradené". */
  pouziteSlotIds?: Set<string>;
  onOpenSklo: () => void;
  /** Označiť v tabuľke riadky s materiálom, hranou alebo kovaním (KOLO 233). */
  onOznac?: (ciel: CielOznacenia, nazov: string) => void;
  /**
   * Zámok (KOLO 231) — materiály zákazky, hrany, kovania a rezanie mení
   * rozpočtár; obchodník ich vidí len na čítanie. Cena nad tabmi ostáva.
   */
  zamok?: string | null;
}

/** Klik na kartu materiálu otvára okno hrán — pri zámku sa zastaví. */
function zastavKlik(e: React.SyntheticEvent): void {
  e.preventDefault();
  e.stopPropagation();
}

/**
 * Bočný panel zákazky — cena nad tabmi, obsah v jednom tabe.
 *
 * Predtým to boli štyri rolety pod sebou: rozbalené mali ~1320 px, kým
 * miesta bolo ~665 px, takže sa muselo scrollovať skoro dve výšky výhľadu.
 * Tab ukáže naraz len jednu vec (najvyšší je „Rezanie" ~400 px).
 * Cena zostáva nad tabmi — je to číslo, ktoré má byť vidno vždy.
 *
 * Panel berie plnú výšku stĺpca (KOLO 56). Od KOLA 63 má **~414 px** (bolo
 * ~761), lebo pás detailu dielca si nad mriežkou berie 325 px — hrany sa
 * klikajú v ňom a musí byť stále na očiach. Sloty, Hrany a Kovania sa
 * zmestia; **tab Rezanie (~400 px) scrolluje** cez `overflow-y-auto` na tele.
 *
 * **Karta s tabmi je od KOLA 100 `sticky`, cena nie.** Keď sa editor do okna
 * nezmestí, scrolluje `main` (`AppShell.tsx`) a s ním odchádzala hlavička
 * zákazky, toolbar **aj celý panel** — k slotom sa user musel vracať hore.
 * Do KOLA 100 tu sticky nebolo a ani by nefungovalo: karta má `lg:flex-1`,
 * teda **presne výšku grid riadku**, a sticky element vysoký ako jeho
 * containing block nemá kam cestovať. Preto k `sticky top-0` patrí **aj
 * `max-h`** — bez neho by sa karta prilepila, ale spodok by trčal pod
 * okrajom obrazovky. `4.5rem` = header `h-14` (56 px, KOLO 174) + `border-b` 1 px +
 * 15 px vzduchu; `top-0` je bez offsetu, lebo scrollport `main` začína
 * presne pod headerom (rovnaký dôvod ako pri páse detailu).
 * Keď sa editor do okna **zmestí**, je to no-op — riadok je nižší než
 * viewport, takže `flex-1` na `max-h` nikdy nenarazí.
 *
 * Cena (`ZakazkaSummary`) zostáva nad tabmi a **odscrolluje** — rozhodnutie
 * usera; taby tým získajú tých ~150 px na sloty.
 */
export function BocnyPanel({
  zakazka,
  varianta,
  sloty,
  pouziteSlotIds,
  onOpenSklo,
  onOznac,
  zamok = null,
}: BocnyPanelProps): JSX.Element {
  const [tab, setTab] = useState<PanelTab>(readTab);
  // Dielce varianty — karta materiálu povie pri Odobrať, koľko ich ho
  // používa (KOLO 233)
  const dielceVarianty = (varianta.zostavy ?? []).flatMap((z) => z.dielce ?? []);

  function zmenTab(v: string): void {
    const t = v as PanelTab;
    setTab(t);
    try {
      localStorage.setItem(LS_TAB, t);
    } catch {
      /* voľba sa neuloží, panel funguje ďalej */
    }
  }

  return (
    <aside
      className="flex min-h-0 flex-col gap-3 lg:h-full"
      data-testid="bocny-panel"
    >
      <div className="shrink-0">
        <ZakazkaSummary zakazka={zakazka} varianta={varianta} />
      </div>

      <div
        className="flex min-h-0 flex-col overflow-hidden rounded-lg border bg-card lg:sticky lg:top-0 lg:z-10 lg:max-h-[calc(100vh-4.5rem)] lg:flex-1"
        data-testid="panel-taby"
      >
        <Tabs value={tab} onValueChange={zmenTab} className="shrink-0">
          <TabsList className="grid h-8 w-full grid-cols-4 rounded-b-none">
            {TABY.map((t) => (
              <TabsTrigger
                key={t.key}
                value={t.key}
                title={t.title}
                className="px-1 text-xs"
              >
                {t.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        <div
          className="min-h-0 flex-1 overflow-y-auto pb-1.5"
          title={zamok ?? undefined}
          onClickCapture={zamok && tab === "sloty" ? zastavKlik : undefined}
        >
          {/* Len na čítanie (KOLO 231): `fieldset disabled` vypne tlačidlá
              a polia všetkých tabov naraz */}
          <fieldset disabled={!!zamok} className="contents">
          {tab === "sloty" ? (
            <SlotList
              zakazkaId={zakazka.id}
              variantaId={varianta.id}
              sloty={sloty}
              pouziteSlotIds={pouziteSlotIds}
              onOpenSklo={onOpenSklo}
              collapsible={false}
              onOznac={
                onOznac
                  ? (s) =>
                      onOznac(
                        { druh: "material", slotId: s.id },
                        `${s.slot_kod} ${s.material?.nazov ?? ""}`.trim(),
                      )
                  : undefined
              }
              dielce={dielceVarianty}
            />
          ) : null}
          {tab === "hrany" ? (
            <EdgesUsageSection
              zakazkaId={zakazka.id}
              variantaId={varianta.id}
              bezStropu
              onOznac={
                onOznac
                  ? (id, nazov) => onOznac({ druh: "hrana", materialId: id }, nazov)
                  : undefined
              }
            />
          ) : null}
          {tab === "kovania" ? (
            <KovaniaUsageSection
              zakazkaId={zakazka.id}
              variantaId={varianta.id}
              bezStropu
              onOznac={
                onOznac
                  ? (id, nazov) => onOznac({ druh: "kovanie", materialId: id }, nazov)
                  : undefined
              }
            />
          ) : null}
          {tab === "rezanie" ? (
            <PorezPanel variantaId={varianta.id} collapsible={false} />
          ) : null}
          </fieldset>
        </div>
      </div>
    </aside>
  );
}
